import React, { useState } from "react";
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  ArrowDownTrayIcon,
} from "@heroicons/react/20/solid";
import Alert from "./Alert";
import { downloadFile } from "../utils/helpers";
import { generateCertificateFilename } from "../../shared/utils/fileHelpers";
import {
  certificateName,
  certificateDisplayName,
} from "../../shared/utils/certificateNaming";
import { CERTIFICATE_EXPIRY_WARNING_DAYS } from "../config/constants";

export default function CertificateChainDiagram({
  chain,
  onCertificateClick,
  // THE BADGE MUST USE THE SAME WORD AS EVERYTHING ELSE ON THE PAGE
  // (2026-08-19 review round 14). It was fixed text reading "Not
  // Installed", while on a pasted file the card above says "Not
  // Supplied" and the note says "was not supplied" - three names for
  // one fact. The route decides the word everywhere else, so it has to
  // decide it here too.
  fromServer = false,
}) {
  // 2026-07-04 operator consistency directive : download failures used
  // to surface via a raw browser alert() ; they now render the shared
  // dismissible Alert above the diagram (the site-standard error
  // mechanism). Hooks must run unconditionally, so this sits before
  // the empty-chain early return.
  const [downloadError, setDownloadError] = useState(null);

  if (!chain || chain.length === 0) {
    return (
      // 2026-07-04 tools-wide overhaul (operator-approved), audit
      // item #5 reviewer sweep : this customer-visible empty state
      // previously read bare lowercase "certificate" - the Trustico®
      // style guide requires "SSL Certificate" (capital C, with the
      // SSL qualifier), and status messages use Title Case.
      <div className="text-center text-muted py-8">
        No SSL Certificate Chain Data Available
      </div>
    );
  }
  const getStatusIcon = (cert) => {
    if (!cert || !cert.info) return null;
    if (cert.info.isExpired) {
      return <XCircleIcon className="w-6 h-6 text-red-500" />;
    }
    if (cert.info.daysUntilExpiry < CERTIFICATE_EXPIRY_WARNING_DAYS) {
      return (
        <ExclamationTriangleIcon className="w-6 h-6 text-yellow-700 dark:text-yellow-400" />
      );
    }
    return (
      <CheckCircleIcon className="w-6 h-6 text-green-700 dark:text-green-400" />
    );
  };

  const getStatusColor = (cert) => {
    if (!cert || !cert.info)
      return "border-gray-500 bg-gray-50 dark:bg-gray-900/50";
    if (cert.info.isExpired)
      return "border-red-500 bg-red-50 dark:bg-red-900/30";
    if (cert.info.daysUntilExpiry < CERTIFICATE_EXPIRY_WARNING_DAYS)
      return "border-yellow-500 bg-yellow-50 dark:bg-yellow-900/30";
    return "border-green-500 bg-green-50 dark:bg-green-900/30";
  };

  // The DISPLAY form, with the "+ N More SANs" count : a box in the
  // diagram is a name standing on its own, which is exactly where the
  // count belongs.
  const getCertificateName = (cert) => {
    if (!cert || !cert.info || !cert.info.subject)
      return "Unknown SSL Certificate";
    return certificateDisplayName(cert.info) || "Unknown SSL Certificate";
  };

  // The same name WITHOUT the count, for anywhere it is read as part of
  // a sentence rather than shown on its own.
  const getCertificateBareName = (cert) => {
    if (!cert || !cert.info || !cert.info.subject)
      return "Unknown SSL Certificate";
    return certificateName(cert.info) || "Unknown SSL Certificate";
  };

  const getCertificateType = (cert) => {
    switch (cert.type) {
      case "root":
        return "Root Certificate (Self-Signed)";
      case "intermediate":
        return "Intermediate Certificate";
      case "leaf":
        return "End Entity Certificate";
      default:
        // 2026-07-04 operator consistency directive : never bare
        // "Certificate" in customer text - the style guide requires the
        // full "SSL Certificate" everywhere.
        return "SSL Certificate";
    }
  };

  const downloadCertificate = (cert, event) => {
    event.stopPropagation(); // Prevent triggering onCertificateClick

    try {
      // 2026-08-07 download/copy consistency pass (operator-directed) :
      // this was a hand-rolled blob/anchor click, the last one in the
      // suite after PasswordGenerator's ; it now runs through the
      // shared downloadFile helper (the filename already followed the
      // standard via generateCertificateFilename). The try/catch and
      // its customer-facing Alert stay : this is the one download site
      // that surfaces a failure to the customer, a deliberate
      // behavior the shared helper does not provide.
      downloadFile(cert.certificate, generateCertificateFilename(cert));
    } catch (error) {
      // 2026-07-04 operator consistency directive : this failure used
      // to surface via a raw browser alert() - the ONLY window.alert on
      // a customer tool page, off-standard in mechanism (the standard
      // is the shared in-page Alert) and, at the time, in casing (errors
      // were UPPERCASE until the 2026-08-27 ruling). The console breadcrumb stays for
      // operators ; the customer sees the standard dismissible Alert
      // rendered above the diagram.
      console.error("FAILED TO DOWNLOAD SSL CERTIFICATE : ", error);
      setDownloadError(
        "The SSL Certificate could not be downloaded. Please try again."
      );
    }
  };

  return (
    <div className="flex flex-col items-center space-y-4 py-8">
      {downloadError && (
        <Alert
          type="error"
          title="Download Failed"
          className="w-full max-w-3xl"
          onClose={() => setDownloadError(null)}
        >
          {downloadError}
        </Alert>
      )}
      {chain.map((cert, index) => {
        if (!cert || !cert.info) return null;
        return (
          <React.Fragment key={index}>
            {/* Certificate Box */}
            <div
              className={`
              relative transition-all duration-200
              border-2 rounded-lg w-full max-w-3xl
              ${getStatusColor(cert)}
              hover:shadow-lg
            `}
            >
              {/* Header Section */}
              <div
                className="p-6 cursor-pointer"
                onClick={() => onCertificateClick(cert)}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-start justify-between">
                      <div>
                        {/*
                          2026-07-04 operator-spotted : this card title
                          (the SSL Certificate name) rendered font-bold
                          where every sibling section heading on the
                          site is text-lg font-semibold - the exact
                          size / weight inconsistency the operator
                          flagged on the Chain Analyzer page.
                        */}
                        <h3 className="text-lg font-semibold text-heading">
                          {getCertificateName(cert)}
                        </h3>
                        <p className="text-body mt-1">
                          {getCertificateType(cert)}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        {getStatusIcon(cert)}
                        <button
                          onClick={(e) => downloadCertificate(cert, e)}
                          className="p-2 text-body hover:text-heading hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                          title="Download SSL Certificate"
                          // THE BARE NAME, because this is a SENTENCE.
                          // The heading above is a name standing alone
                          // and takes the count ; a screen reader
                          // reading this label would otherwise announce
                          // "Download *.trustico.com + 59 More SANs SSL
                          // Certificate". The visible title says only
                          // "Download SSL Certificate", so the mangling
                          // would have reached assistive technology
                          // alone - the users least able to work around
                          // it.
                          aria-label={`Download ${getCertificateBareName(cert)} SSL Certificate`}
                        >
                          <ArrowDownTrayIcon className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Status Bar */}
                <div className="flex items-center gap-4">
                  <div
                    className={`font-medium ${cert.info.isExpired ? "text-red-700 dark:text-red-300" : "text-green-700 dark:text-green-400"}`}
                  >
                    {cert.info.isExpired
                      ? "● Expired"
                      : `● Valid for ${cert.info.daysUntilExpiry} days`}
                  </div>
                  {/*
                    "Not Installed" (operator ruling 2026-08-19). The
                    label was "Auto-fetched", which said HOW we got the
                    certificate and left the customer to work out that
                    it therefore was not theirs. Whose it is, is the
                    only part that matters on a page showing them what
                    they have installed - and only the ones that are NOT
                    theirs are badged, so anything unmarked came from
                    their server and the two can never be confused.
                  */}
                  {cert.obtainedFromStore && (
                    <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-1 rounded-sm">
                      {fromServer ? "Not Installed" : "Not Supplied"}
                    </span>
                  )}
                  {/* Which SSL Certificate the "you can remove it"
                      note means. It is the only thing on the page that
                      says WHICH one, and the note itself cannot name it
                      without listing certificates the customer would
                      then have to match up by eye. */}
                  {cert.notPartOfChain && (
                    <span className="text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 px-2 py-1 rounded-sm">
                      Not Part of This Chain
                    </span>
                  )}
                </div>
              </div>

              {/* Certificate Details Grid */}
              <div className="bg-gray-100/50 dark:bg-gray-900/40 p-4 border-t border-gray-200 dark:border-gray-700">
                <div className="space-y-3">
                  {/* Two column row */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Left Column - Signature Algorithm */}
                    <div>
                      <span className="text-xs font-medium text-muted uppercase tracking-wider">
                        Signature Algorithm
                      </span>
                      <p className="text-heading">
                        {cert.info.signatureAlgorithm}
                      </p>
                    </div>

                    {/* Right Column - Key Algorithm */}
                    <div>
                      <span className="text-xs font-medium text-muted uppercase tracking-wider">
                        Key Algorithm
                      </span>
                      <p className="text-heading">
                        {cert.info.publicKey?.algorithm || "Unknown"}
                        {cert.info.publicKey?.bits &&
                          ` (${cert.info.publicKey.bits} bits)`}
                        {cert.info.publicKey?.curve &&
                          ` (${cert.info.publicKey.curve})`}
                      </p>
                    </div>
                  </div>

                  {/* Validity Period */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <span className="text-xs font-medium text-muted uppercase tracking-wider">
                        Not Before
                      </span>
                      <p className="text-heading">
                        {cert.info.notBefore
                          ? new Date(cert.info.notBefore).toLocaleDateString()
                          : "N/A"}
                      </p>
                    </div>
                    <div>
                      <span className="text-xs font-medium text-muted uppercase tracking-wider">
                        Not After
                      </span>
                      <p className="text-heading">
                        {cert.info.notAfter
                          ? new Date(cert.info.notAfter).toLocaleDateString()
                          : "N/A"}
                      </p>
                    </div>
                  </div>

                  {/* Organization and Issued By - 2 column row */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Organization - Left Column */}
                    {(cert.info.subject?.organizationName ||
                      cert.info.subject?.organizationalUnitName ||
                      cert.info.subject?.countryName ||
                      cert.info.subject?.stateOrProvinceName ||
                      cert.info.subject?.localityName) && (
                      <div>
                        <span className="text-xs font-medium text-muted uppercase tracking-wider">
                          Organization
                        </span>
                        <p className="text-heading">
                          {[
                            cert.info.subject?.organizationName,
                            cert.info.subject?.organizationalUnitName,
                            [
                              cert.info.subject?.localityName,
                              cert.info.subject?.stateOrProvinceName,
                              cert.info.subject?.countryName,
                            ]
                              .filter(Boolean)
                              .join(", "),
                          ]
                            .filter(Boolean)
                            .join(" - ")}
                        </p>
                      </div>
                    )}

                    {/* Issued by - Right Column (or left if no org) */}
                    {cert.info.issuer?.commonName && cert.type !== "root" && (
                      <div>
                        <span className="text-xs font-medium text-muted uppercase tracking-wider">
                          Issued by
                        </span>
                        <p
                          className="text-heading truncate"
                          title={cert.info.issuer?.commonName || ""}
                        >
                          {cert.info.issuer?.commonName || "Unknown Issuer"}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Key Usage - only show if available */}
                  {cert.info.extensions?.keyUsage &&
                    Array.isArray(cert.info.extensions.keyUsage) &&
                    cert.info.extensions.keyUsage.length > 0 && (
                      <div>
                        <span className="text-xs font-medium text-muted uppercase tracking-wider">
                          Key Usage
                        </span>
                        <p className="text-heading">
                          {cert.info.extensions.keyUsage.join(", ")}
                        </p>
                      </div>
                    )}

                  {/* Full width row for serial number */}
                  <div>
                    <span className="text-xs font-medium text-muted uppercase tracking-wider">
                      Serial Number
                    </span>
                    <p className="text-heading font-mono mt-1">
                      {cert.info.serialNumber}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Connection Arrow */}
            {index < chain.length - 1 && (
              <div className="relative py-3">
                <svg
                  className="w-10 h-10 text-gray-400 dark:text-gray-500 mx-auto"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 17l-4 4m0 0l-4-4m4 4V3"
                  />
                </svg>
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}
